/**
 * BattleKart is temporarily closed but returns in February — flip
 * BATTLEKART_ENABLED to true to restore the full BattleKart booking flow.
 * Everything BattleKart (components, state slices, styling, cart handling)
 * is intact; the closure lives in exactly four places:
 *
 *  - THIS FILE: the flag, the closed-page URL, and all decision logic
 *    (handleBattleKartClosureAtEntry + shouldSkipExperienceSelector)
 *  - src/index.tsx: one call to handleBattleKartClosureAtEntry before React
 *    mounts — full-page loads of BattleKart URLs leave for the closed page
 *    with no UI ever painting, and ?includeBK is scrubbed from the landing
 *    URL so the multi-track queue can never pick BattleKart up
 *  - src/app/Router.tsx: the /battlekart/* screens are unrouted (with
 *    an in-app fallback redirect, e.g. a stale BattleKart cart pill), and any
 *    transition through the skipped "/" selector renders instantly
 *    (direction 0) instead of slide-animating
 *  - src/components/TrackSelection: hides the BattleKart tile and forwards
 *    "/" to the racing age-selection screen pre-paint. It must still MOUNT
 *    at "/" because its fetchData seeds the metaobject seats/races defaults.
 *
 * Shopify-side (outside this repo): the /pages/battle-kart-booking page and
 * the *bk marketing pages get Shopify URL redirects to the closed page, and
 * the homepage widget drops its BattleKart option (the embed's BattleKart
 * route is deliberately not gated here — it is host-driven and BattleKart
 * products are unpublished while closed). Reverse those when re-enabling.
 */
import { BOOKING_PARAM_KEYS, LEGACY_ROUTE_SLUGS, ROUTE_SLUGS } from "@/shared/utils/bookingUrl";

export const BATTLEKART_ENABLED: boolean = false;

/**
 * Active Kids voucher capture. On by default; set REACT_APP_ACK_ENABLED=false to
 * switch it off if Service NSW becomes unreliable. Disabling hides the opt-in and
 * stops the voucher step being routed to, leaving the rest of the booking flow
 * untouched — an in-progress session still settles, because the server side has
 * its own ACK_ENABLED.
 */
export const isActiveKidsEnabled = (): boolean =>
  (process.env.REACT_APP_ACK_ENABLED ?? "").toLowerCase() !== "false";

/**
 * Where removed BattleKart URLs send visitors while the attraction is closed.
 */
export const BATTLEKART_CLOSED_URL = "https://hyperkarting.com.au/pages/battlekart";

/**
 * While BattleKart is closed the root experience selector has only Racing
 * left to offer, so "/" forwards straight to the racing age-selection screen.
 * Two cases still render the selector screen:
 *  - the add-another-attraction upsell view (Racing + Arcade tiles)
 *  - the bosshunting partner host, whose variant never offered BattleKart
 * Both TrackSelection (to actually forward) and the Router (to make any
 * transition through "/" instant instead of slide-animated) consult this
 * one helper, so the two can never disagree.
 */
export const shouldSkipExperienceSelector = (opts: { isAnyItemsAvailableInCart: boolean; showArcadeBanner: boolean; appHostedAt?: string | null }): boolean => {
  if (BATTLEKART_ENABLED) return false;
  if (opts.isAnyItemsAvailableInCart && opts.showArcadeBanner) return false;
  if (opts.appHostedAt === "bosshunting") return false;
  return true;
};

/**
 * Pre-React handling of the BattleKart closure for the initial document load.
 * Returns true when the browser is leaving for the closed page — the caller
 * must then skip mounting React so no booking UI (e.g. the BattleKart-themed
 * progress header) ever paints while the navigation is in flight.
 *
 * Redirects on:
 *  - /battlekart[/*] after the embed page's basename (any host), and the
 *    superseded /battle-kart-track spelling
 *  - the legacy ?selectedRoute=battle-kart-track query routing
 *  - the BattleKart marketing pages (/pages/battle-kart-booking, "…NNNNNNbk")
 *
 * Otherwise scrubs the includeBK param from the landing URL: it would queue
 * BattleKart after the racing flow (RacingTrackSelection's multi-track
 * entries), and removing it here means no in-flow code needs to know
 * BattleKart is closed. In-app navigation can't produce it — the tile that
 * sets it is hidden. Both the short wire key and the long name are scrubbed
 * since inbound links can carry either.
 *
 * Runs on the landing URL before `applyCanonicalBookingUrl` rewrites it, so
 * every legacy spelling is still intact here.
 */
export const handleBattleKartClosureAtEntry = (mountElement: HTMLElement | null): boolean => {
  if (BATTLEKART_ENABLED) return false;
  // The homepage widget mounts with a MemoryRouter — the browser URL is not
  // a booking route there, so never rewrite the host page out from under it.
  if (mountElement?.dataset.component === "product-selector") return false;

  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);

  const battleKartRouteSlugs = [ROUTE_SLUGS.BATTLE_KART, ...Object.keys(LEGACY_ROUTE_SLUGS).filter((slug) => LEGACY_ROUTE_SLUGS[slug] === ROUTE_SLUGS.BATTLE_KART)];
  const selectedRoute = params.get("selectedRoute");

  const isBattleKartUrl =
    battleKartRouteSlugs.some((slug) => new RegExp(`(^|/)${slug}(/|$)`).test(pathname)) ||
    (selectedRoute !== null && battleKartRouteSlugs.includes(selectedRoute)) ||
    pathname.endsWith("/pages/battle-kart-booking") ||
    /\d{6}bk$/.test(pathname);

  if (isBattleKartUrl) {
    window.location.replace(BATTLEKART_CLOSED_URL);
    return true;
  }

  const includeBKKeys = ["includeBK", BOOKING_PARAM_KEYS.includeBK];
  if (includeBKKeys.some((key) => params.has(key))) {
    includeBKKeys.forEach((key) => params.delete(key));
    const query = params.toString();
    window.history.replaceState(null, "", pathname + (query ? "?" + query : "") + hash);
  }

  return false;
};
